import { BOARD_DIMENSIONS, Board, BoardDimension, Panel } from "../Materials";
import { isBoard } from "../board-helpers";
import { isPanel } from "../panel-helpers";
import { makeMaterial } from "../material-helpers";
import { InputMaterialWithQuantity, MachineType, Operation } from "../Machine";
import { GENERATED_COLLISION_SHAPES } from "../machine-collision-boxes.generated";

/** The next detent up the thickness scale, or undefined at the top. */
export function thicknessStepAbove(
  dimension: BoardDimension,
): BoardDimension | undefined {
  return BOARD_DIMENSIONS[BOARD_DIMENSIONS.indexOf(dimension) + 1];
}

/** The next detent down the thickness scale, or undefined at the bottom. */
export function thicknessStepBelow(
  dimension: BoardDimension,
): BoardDimension | undefined {
  return BOARD_DIMENSIONS[BOARD_DIMENSIONS.indexOf(dimension) - 1];
}

/**
 * The planer has no modes — like the real thing, its whole interface is a
 * power switch, a height crank, and stock fed in from your hands. One
 * operation covers boards and panels alike (the cutter head can't tell),
 * and each pass takes at most one thickness detent off: deeper cuts mean
 * sequential passes, cranking the head down between each.
 */
export const lunchboxPlaner: MachineType = {
  id: "lunchboxPlaner",
  name: "Planer",
  description:
    "A portable thickness planer. Planes a board down to a set thickness, one pass at a time.",
  // A lunchbox planer on its stand: about 19" × 26", so a 2×3-ft
  // footprint with stock feeding the long way (see the measured
  // collision box for the exact solid area).
  cellsOccupied: [
    [0, -1],
    [0, 0],
    [0, 1],
    [1, -1],
    [1, 0],
    [1, 1],
  ],
  collisionShapes: GENERATED_COLLISION_SHAPES.lunchboxPlaner,
  // Feed-through: room to stand at the infeed and to catch stock at the
  // outfeed — a planer can't back onto a wall
  freeCellsNeeded: [
    [0, 2],
    [1, 2],
    [0, -2],
    [1, -2],
  ],
  operationPosition: [0, 2],
  outputPosition: [0, -2],
  cost: 450,
  materialStorage: 0,
  toolSlots: 1,
  // One board in the throat at a time. Unlike the others there's nothing
  // to line up afterwards: the rollers grab it the moment it's set down.
  inputSpaces: 1,
  directFeed: true,
  feedsThrough: true,
  // Small enough to mount on a worktable cell instead of the floor
  benchtop: true,
  powerSwitch: true,
  corded: true,
  operations: [
    {
      id: "plane",
      requiredSkill: "basicMilling",
      name: "Plane",
      duration: 8,
      dustOutput: 4,
      // Feed rollers pull the board through on their own — once it's in,
      // the pass finishes whether or not anyone is standing there
      powerFeed: true,
      parameters: [
        {
          id: "targetThickness",
          name: "Cut Height",
          values: BOARD_DIMENSIONS,
          unit: "/4",
        },
      ],
      getInputMaterials: (params) => {
        const cutHeight = params.targetThickness as BoardDimension;
        // Equal thickness is a skim pass: rough stock carries sacrificial
        // material beyond its nominal size, so milling never shrinks the
        // listed dimensions. One detent above is a full-depth bite; any
        // thicker won't fit under the cutter head at this setting.
        const above = thicknessStepAbove(cutHeight);
        return [
          {
            type: ["board", "panel"],
            thickness: above === undefined ? [cutHeight] : [cutHeight, above],
            quantity: 1,
            matches: (material) =>
              isBoard(material)
                ? // A planer needs a flat reference face — it can't fix warp,
                  // only copy flatness to the other side. Joint a face first.
                  material.jointedFaces >= 1
                : // Never feed end grain into a planer — it tears out in
                  // chunks. Sanding is the only way to flatten an end-grain
                  // panel.
                  isPanel(material) && material.grain !== "end",
            // InputMaterial distributes over the material union, so a
            // requirement accepting several types can't typecheck as one
            // member — the one place the spec needs a cast.
          } as InputMaterialWithQuantity,
        ];
      },
      explainRejection: (material, params) => {
        // The workflow prerequisite comes first: thickness only matters
        // once the stock has a face to ride the bed on
        if (isBoard(material) && material.jointedFaces === 0) {
          return "No flat reference face — a planer copies flatness to the far side, it can't create it. Joint a face first.";
        }
        if (isPanel(material) && material.grain === "end") {
          return "End grain tears out under a cutter head — an end-grain panel gets flattened by sanding instead.";
        }
        if (!isBoard(material) && !isPanel(material)) {
          return null;
        }
        const cutHeight = params?.targetThickness as BoardDimension;
        const bite = thicknessStepAbove(cutHeight);
        if (bite !== undefined && material.thickness > bite) {
          return `Won't fit under the cutter head — raise the cut height to ${thicknessStepBelow(material.thickness)}/4 for the first pass.`;
        }
        if (material.thickness < cutHeight) {
          return `The cutter head is set above the stock — lower the cut height to ${material.thickness}/4 to surface it.`;
        }
        return null;
      },
      output: (materials, params) => {
        const stock = materials[0];
        if (!isBoard(stock) && !isPanel(stock)) {
          throw new Error("Input material is not a board or panel");
        }
        const cutHeight = params.targetThickness as BoardDimension;
        // One detent per pass, never more. Equal thickness is a skim: same
        // size out, freshly surfaced.
        const planedThickness =
          stock.thickness > cutHeight
            ? (thicknessStepBelow(stock.thickness) ?? stock.thickness)
            : stock.thickness;
        // Freshly surfaced either way (but only sanding reaches "sanded");
        // a board's faces additionally come out parallel.
        return {
          inputs: [],
          outputs: [
            isBoard(stock)
              ? makeMaterial<Board>({
                  ...stock,
                  thickness: planedThickness,
                  jointedFaces: 2,
                  surface: "smooth",
                })
              : makeMaterial<Panel>({
                  ...stock,
                  thickness: planedThickness,
                  surface: "smooth",
                }),
          ],
        };
      },
    },
  ],
};
